import { useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { PricingWaterfall } from '../types';
import { pickupDeltaPoints, pickupPointsToDollars } from '../pickupPricingMath';
import { buildPickupWaterfallReconciliation } from '../pickupWaterfallModel';
import { cn } from '@/lib/utils';

interface ProductDetail {
  investor?: string | null;
  loCompensationDollars?: number | null;
  loCompensationPercent?: number | null;
  closingCost?: number | null;
}

interface PriceWaterfallPopoverProps {
  type: 'locked' | 'current' | 'bestAlt';
  price: number | null;
  waterfall: PricingWaterfall | null;
  lockedInvestorPrice?: number | null;
  productDetail?: ProductDetail | null;
  comparison?: {
    lockPrice: number | null;
    currentPrice: number | null;
    bestAltPrice: number | null;
    loanAmount: number | null;
  };
  portalContainer?: HTMLElement | null;
  className?: string;
  children?: React.ReactNode;
}

function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

function formatPrice(price: number | null) {
  if (isNil(price)) {
return '-';
}
  return price.toFixed(3);
}

function formatCurrency(amount: number | null | undefined) {
  if (isNil(amount)) {
return '-';
}
  const neg = amount < 0;
  const abs = Math.abs(amount);
  const formatted = abs >= 1000
    ? `$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : `$${abs.toFixed(0)}`;
  return neg ? `-${formatted}` : formatted;
}

function formatAdj(val: number) {
  const sign = val >= 0 ? '+' : '';
  return `${sign}${val.toFixed(3)}`;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-2 first:mt-0">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function AdjRow({ reason, value }: { reason: string; value: number }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-3 text-xs items-baseline">
      <span className="text-muted-foreground truncate">{reason}</span>
      <span className={cn('font-mono tabular-nums text-right whitespace-nowrap', value > 0 ? 'text-emerald-600' : value < 0 ? 'text-red-600' : '')}>
        {formatAdj(value)}
      </span>
    </div>
  );
}

function DeltaRow({ label, points, loanAmount }: { label: string; points: number | null; loanAmount: number | null }) {
  const dollars = pickupPointsToDollars(points, loanAmount);

  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 text-xs items-baseline">
      <span className="text-muted-foreground truncate">{label}</span>
      <span className={cn('font-mono tabular-nums text-right whitespace-nowrap', !isNil(points) && points > 0 ? 'text-emerald-600' : !isNil(points) && points < 0 ? 'text-red-600' : '')}>
        {isNil(points) ? '-' : formatAdj(points)}
      </span>
      <span className={cn('font-mono tabular-nums text-muted-foreground text-right whitespace-nowrap min-w-[60px]', !isNil(dollars) && dollars > 0 ? 'text-emerald-600' : !isNil(dollars) && dollars < 0 ? 'text-red-600' : '')}>
        {isNil(dollars) ? '-' : formatCurrency(dollars)}
      </span>
    </div>
  );
}

function ValueRow({ label, value, dollarValue, className }: { label: string; value: string; dollarValue?: string; className?: string }) {
  return (
    <div className={cn('grid grid-cols-[1fr_auto_auto] gap-x-2 text-xs items-baseline', className)}>
      <span className="text-muted-foreground truncate">{label}</span>
      <span className="font-mono tabular-nums text-right whitespace-nowrap">{value}</span>
      {dollarValue ? (
        <span className="font-mono tabular-nums text-muted-foreground text-right whitespace-nowrap min-w-[60px]">{dollarValue}</span>
      ) : (
        <span className="min-w-[60px]" />
      )}
    </div>
  );
}

/** Exported for testing */
export function LockedContent({
  waterfall,
  price,
  lockedInvestorPrice
}: {
  waterfall: PricingWaterfall;
  price: number | null;
  lockedInvestorPrice?: number | null;
}) {
  const pd = waterfall.pricingDetail;
  const priceAdj = waterfall.adjustments.filter(a => a.adjustmentType === 'Price');
  const invAdj = waterfall.investorAdjustments.filter(a => a.adjustmentType === 'Price');
  const loCompMarkup = waterfall.markups.find(m => m.name === 'LO Compensation');
  const otherMarkups = waterfall.markups.filter(m => m.name !== 'LO Compensation');
  const investorPrice = lockedInvestorPrice ?? waterfall.investorPrice ?? null;
  const showInvestorPrice = !isNil(investorPrice) && !isNil(price) && Math.abs(investorPrice - price) >= 0.001;
  const reconciliation = buildPickupWaterfallReconciliation(waterfall, price);

  return (
    <div
      className="space-y-1"
      data-pickup-waterfall-summary
      data-pickup-waterfall-base={reconciliation.basePrice?.toFixed(3)}
      data-pickup-waterfall-net={reconciliation.netPrice?.toFixed(3)}
      data-pickup-waterfall-reconciles={String(reconciliation.reconciles)}
    >
      <Section label="Pickup basis">
        <ValueRow label="Lock Price" value={formatPrice(price)} className="font-semibold" />
        {!isNil(pd?.parPrice) && Math.abs(pd.parPrice - (price ?? pd.parPrice)) >= 0.001 && (
          <ValueRow label="Par Price" value={pd.parPrice.toFixed(3)} />
        )}
        {!isNil(reconciliation.reconciliationPoints)
          && Math.abs(reconciliation.reconciliationPoints) >= 0.0005 && (
          <ValueRow
            label="Other / reconciliation"
            value={formatAdj(reconciliation.reconciliationPoints)}
          />
        )}
      </Section>

      {pd && (
        <Section label="Pricing detail">
          {!isNil(pd.discountDollars) && pd.discountDollars !== 0 && (
            <ValueRow
              label={pd.discountDollars < 0 ? 'Lender Credit' : 'Discount Points'}
              value={formatCurrency(Math.abs(pd.discountDollars))}
            />
          )}
          {!isNil(pd.loCompensationPercent) && (
            <ValueRow
              label={`LO Comp (${pd.loCompensationPercent}%)`}
              value={formatCurrency(pd.loCompensationDollars)}
            />
          )}
        </Section>
      )}

      {(showInvestorPrice || priceAdj.length > 0 || invAdj.length > 0 || otherMarkups.length > 0 || loCompMarkup) && (
        <Section label="Investor price context">
          {showInvestorPrice && (
            <ValueRow label="Investor Price" value={formatPrice(investorPrice)} className="font-medium" />
          )}
          {priceAdj.map((a, i) => (
            <AdjRow key={`pa-${i}`} reason={a.reason} value={a.adjustor} />
          ))}
          {invAdj.map((a, i) => (
            <AdjRow key={`ia-${i}`} reason={a.reason} value={a.adjustor} />
          ))}
          {otherMarkups.map((m, i) => (
            <ValueRow
              key={`m-${i}`}
              label={m.name}
              value={formatAdj(m.netMarkupPercent)}
              dollarValue={!isNil(m.netMarkupDollars) ? formatCurrency(m.netMarkupDollars) : undefined}
            />
          ))}
          {loCompMarkup && waterfall.compensation && (
            <ValueRow
              label={`LO Comp (${waterfall.compensation.compensationPercent}%)`}
              value={formatAdj(loCompMarkup.netMarkupPercent)}
              dollarValue={formatCurrency(
                waterfall.compensation.loCompensationNetDollarAmount
                ?? waterfall.compensation.loCompensationDollarAmount
              )}
            />
          )}
        </Section>
      )}

      {pd?.pricingLastUpdated && (
        <div className="text-[10px] text-muted-foreground mt-1">
          Priced: {new Date(pd.pricingLastUpdated).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
}

/** Exported for testing */
export function CurrentAltContent({
  type,
  productDetail,
  price,
  comparison
}: {
  type: 'current' | 'bestAlt';
  productDetail?: ProductDetail | null;
  price: number | null;
  comparison?: PriceWaterfallPopoverProps['comparison'];
}) {
  const currentVsLock = comparison
    ? pickupDeltaPoints(comparison.currentPrice, comparison.lockPrice)
    : null;
  const altVsLock = comparison
    ? pickupDeltaPoints(comparison.bestAltPrice, comparison.lockPrice)
    : null;
  const altVsCurrent = comparison
    ? pickupDeltaPoints(comparison.bestAltPrice, comparison.currentPrice)
    : null;

  return (
    <div className="space-y-1">
      {productDetail && (!isNil(productDetail.loCompensationPercent) || !isNil(productDetail.loCompensationDollars)) && (
        <Section label="Per-product">
          {(!isNil(productDetail.loCompensationPercent) || !isNil(productDetail.loCompensationDollars)) && (
            <ValueRow
              label={!isNil(productDetail.loCompensationPercent) ? `LO Comp (${productDetail.loCompensationPercent}%)` : 'LO Comp'}
              value={formatCurrency(productDetail.loCompensationDollars)}
            />
          )}
        </Section>
      )}

      <div className="border-t border-border pt-1.5 mt-2">
        <ValueRow label="Price" value={formatPrice(price)} className="font-semibold" />
      </div>

      {comparison && (
        <Section label="Deltas">
          {type === 'current' ? (
            <DeltaRow label="Current vs Lock" points={currentVsLock} loanAmount={comparison.loanAmount} />
          ) : (
            <>
              <DeltaRow label="Alt vs Lock" points={altVsLock} loanAmount={comparison.loanAmount} />
              <DeltaRow label="Alt vs Current" points={altVsCurrent} loanAmount={comparison.loanAmount} />
            </>
          )}
        </Section>
      )}
    </div>
  );
}

const LABELS: Record<string, string> = {
  locked: 'Lock Price Breakdown',
  current: 'Current Price',
  bestAlt: 'Best Alt Price'
};

export function PriceWaterfallPopover({
  type,
  price,
  waterfall,
  lockedInvestorPrice,
  productDetail,
  comparison,
  portalContainer,
  className,
  children
}: PriceWaterfallPopoverProps) {
  const [open, setOpen] = useState(false);

  const canShowPopover = type === 'locked'
    ? !!waterfall
    : !!productDetail || !!comparison;

  if (!canShowPopover) {
    return <span className={className}>{children ?? formatPrice(price)}</span>;
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            'border-b border-dashed border-muted-foreground/40 hover:border-foreground transition-colors cursor-help bg-transparent p-0 font-inherit text-inherit',
            className
          )}
        >
          {children ?? formatPrice(price)}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
        <PopoverPrimitive.Content
          className="bg-popover text-popover-foreground z-50 w-80 rounded-md border p-3 text-xs shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          side="top"
          sideOffset={6}
        >
          <div className="text-xs font-semibold mb-2">{LABELS[type]}</div>
          {type === 'locked' && waterfall ? (
            <LockedContent
              waterfall={waterfall}
              price={price}
              lockedInvestorPrice={lockedInvestorPrice}
            />
          ) : (
            <CurrentAltContent
              type={type === 'bestAlt' ? 'bestAlt' : 'current'}
              productDetail={productDetail}
              price={price}
              comparison={comparison}
            />
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
